import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../configuration';
import {
  DocumentType,
  VerificationRequest,
  VerificationStatus,
} from '../../../models/verification-request.model';
import { submitVerificationRequest } from '../../../api/admin/user.api';
import toast from 'react-hot-toast';
import { ShieldCheck, LifeBuoy } from 'lucide-react';
import { EkycCameraModal } from '../../donate-goal/ekyc-camera-modal';
import { getEkycStatus } from '../../../api/user/ekyc.api';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { useConsentGate } from '../../../hooks/use-consent-gate';
import EligibilityChecklist from '../../../components/eligibility/eligibility-checklist';
import { ekycErrorMessage } from '../../donate-goal/ekyc-flow';
import { fetchProfile } from '../../../redux/features/auth/slice';

// Xin tích xanh Content Creator. KHÔNG thu/lưu ảnh CCCD thủ công (rủi ro pháp lý sau 01/7/2024).
// Danh sách điều kiện do máy chủ kiểm; bắt buộc duy nhất là đã mở nhận donate (bước đó đã gồm eKYC),
// nên người chưa mở donate được chỉ sang Cài đặt Donate trước. Gửi được khi đạt nhóm bắt buộc và
// trạng thái yêu cầu cho phép (chưa gửi, hoặc bị từ chối và đã tới ngày gửi lại).
const ContactSupport = () => {
  const { get } = useFeatureFlags();
  const supportEmail = get('support.email').trim();
  const supportLink = get('support.link').trim();
  const profile = useSelector(
    (state: RootState) => state.features.auth.profile
  );
  const dispatch = useDispatch<AppDispatch>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ekycOpen, setEkycOpen] = useState(false);
  // null: chưa biết. Chỉ hiện ô eKYC khi máy chủ báo rõ là chưa xác minh.
  const [ekycVerified, setEkycVerified] = useState<boolean | null>(null);
  // Hàm tải lại danh sách điều kiện, lấy từ khung điều kiện để gọi sau khi gửi xong.
  const reloadRef = useRef<() => void>(() => undefined);
  const [formData, setFormData] = useState<VerificationRequest>({
    nameOrther: profile?.username || '',
    nationName: '',
    referenceLinks: [''],
    status: VerificationStatus.Pending,
    documentType: DocumentType.IdentityCard,
    documentLink: '',
  });

  useEffect(() => {
    getEkycStatus()
      .then((st) => setEkycVerified(!!st?.isVerified))
      .catch(() => {});
  }, []);

  const creatorGate = useConsentGate('creator-payout');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Bấm đồng ý Điều khoản Creator & Quyết toán trước khi gửi yêu cầu.
    creatorGate.run(doSubmit);
  };

  const doSubmit = async () => {
    try {
      setIsSubmitting(true);
      // Chỉ gửi thông tin creator, không kèm ảnh giấy tờ.
      await submitVerificationRequest({ ...formData, documentLink: '' });
      toast.success('Bạn đã gửi yêu cầu. Cab sẽ xem và trả lời bạn sớm.');
      reloadRef.current();
      dispatch(fetchProfile());
    } catch (error: any) {
      toast.error(ekycErrorMessage(error, 'Cab chưa gửi được yêu cầu. Bạn thử lại sau nhé.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    linkIndex?: number
  ) => {
    const { name, value } = e.target;
    if (name === 'referenceLinks' && typeof linkIndex === 'number') {
      const newLinks = [...formData.referenceLinks];
      newLinks[linkIndex] = value;
      setFormData((prev) => ({ ...prev, referenceLinks: newLinks }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const addLinkField = () => {
    setFormData((prev) => ({
      ...prev,
      referenceLinks: [...prev.referenceLinks, ''],
    }));
  };

  const removeLinkField = (index: number) => {
    if (formData.referenceLinks.length > 1) {
      setFormData((prev) => ({
        ...prev,
        referenceLinks: prev.referenceLinks.filter((_, i) => i !== index),
      }));
    }
  };

  return (
    <div className="bg-white dark:bg-dark2 shadow-lg dark:shadow-none rounded-2xl w-full max-w-[976px]">
      <p className="text-[#62677D] dark:text-slate-300 font-medium text-base px-3">
        Tích xanh creator giúp người xem nhận ra kênh chính chủ của bạn. Bạn xem các điều kiện bên
        dưới, đạt nhóm bắt buộc thì gửi yêu cầu để Cab xem hồ sơ.
      </p>

      {(supportEmail || supportLink) && (
        <div className="mx-3 mt-3 flex flex-wrap items-center gap-3 rounded-lg bg-blue-50 dark:bg-slate-800 px-3 py-2 text-sm">
          <LifeBuoy size={16} className="text-blue-600" />
          <span className="text-gray-600 dark:text-slate-300">Cần hỗ trợ?</span>
          {supportEmail && (
            <a href={`mailto:${supportEmail}`} className="text-blue-600 hover:underline">
              {supportEmail}
            </a>
          )}
          {supportLink && (
            <a href={supportLink} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
              Liên hệ hỗ trợ
            </a>
          )}
        </div>
      )}

      <div className="px-3 pt-4">
        <EligibilityChecklist
          kind="creator"
          submitLabel="Gửi yêu cầu xác minh"
          renderSubmit={(slot) => {
            reloadRef.current = slot.reload;
            return (
            <form onSubmit={handleSubmit}>
              <div className="p-6 flex flex-col gap-4">
                <div className="space-y-2">
                  <label htmlFor="nameOrther" className="block font-medium text-gray-700 dark:text-slate-300">
                    Tên hiển thị
                  </label>
                  <input
                    type="text"
                    id="nameOrther"
                    name="nameOrther"
                    value={formData.nameOrther}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="nationName" className="block font-medium text-gray-700 dark:text-slate-300">
                    Quốc gia
                  </label>
                  <input
                    type="text"
                    id="nationName"
                    name="nationName"
                    value={formData.nationName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="block font-medium text-gray-700 dark:text-slate-300">
                    Liên kết tham khảo
                  </label>
                  {formData.referenceLinks.map((link, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <input
                        type="url"
                        name="referenceLinks"
                        value={link}
                        onChange={(e) => handleChange(e, index)}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-800 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Bạn dán link trang cá nhân của mình, ví dụ Facebook hoặc Instagram"
                        required
                      />
                      {formData.referenceLinks.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeLinkField(index)}
                          className="px-3 py-2 text-red-600 hover:text-red-800"
                        >
                          Xóa
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addLinkField}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                  >
                    + Thêm liên kết khác
                  </button>
                </div>
              </div>

              <div className="p-6 flex justify-between items-center">
                <p className="text-base font-medium px-3 max-w-[636px] text-[#62677D] dark:text-slate-300">
                  Bằng cách gửi yêu cầu này, bạn xác nhận rằng thông tin được cung cấp là chính xác và
                  đồng ý với các điều khoản và điều kiện của chúng tôi.
                </p>

                <button
                  type="submit"
                  disabled={isSubmitting || !slot.enabled}
                  className={`px-6 py-2 bg-blue-600 text-nowrap text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    isSubmitting || !slot.enabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? 'Đang gửi…' : slot.label}
                </button>
              </div>
            </form>
            );
          }}
        />
      </div>

      {ekycVerified === false && (
        <div className="mx-3 mt-4 rounded-lg border border-blue-200 dark:border-slate-600 bg-blue-50 dark:bg-slate-800 p-3 flex items-start gap-3">
          <ShieldCheck className="text-blue-600 mt-0.5 shrink-0" size={20} />
          <div className="text-sm">
            <div className="font-medium text-gray-800 dark:text-white">Xác minh danh tính eKYC</div>
            <p className="text-gray-600 dark:text-slate-300 mt-0.5">
              Tài khoản của bạn chưa xác minh danh tính. Cab không lưu ảnh giấy tờ của bạn.
            </p>
            <button
              type="button"
              onClick={() => setEkycOpen(true)}
              className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Xác minh danh tính ngay
            </button>
          </div>
        </div>
      )}

      <EkycCameraModal
        open={ekycOpen}
        identityOnly
        onClose={() => setEkycOpen(false)}
        onVerified={() => {
          setEkycVerified(true);
          setEkycOpen(false);
          toast.success('Bạn đã xác minh danh tính xong.');
        }}
      />
      {creatorGate.modal}
    </div>
  );
};

export default ContactSupport;
